import { getPriceTagPropsFromPriceDetails } from 'src/utils/productHelpers'
import { RegItem } from 'src/types/regItem'
import { AppliedSortsAndFilters } from './RegistryGiftGiverPage.types'
import { AppliedSortsAndFiltersContextProvider } from '../../contexts/AppliedSortsAndFiltersContext'

export const defaultAppliedSortsAndFilters: AppliedSortsAndFilters = {
  filters: {
    category: 'All',
    price: undefined,
    stores: undefined,
  },
  sort: 'category',
}

export const mergeSortsAndFilters = (
  changes: Partial<AppliedSortsAndFilters>,
  currentSortsAndFilters: AppliedSortsAndFilters
) => {
  const { filters } = changes
  const { filters: prevFilters, sort: prevSort } = currentSortsAndFilters

  return {
    filters: {
      category: applyChangeIfPresent(filters, 'category', prevFilters.category),
      price: applyChangeIfPresent(filters, 'price', prevFilters.price),
      stores: applyChangeIfPresent(filters, 'stores', prevFilters.stores),
    },
    sort: applyChangeIfPresent(changes, 'sort', prevSort),
  }
}

const applyChangeIfPresent = (
  changes: Record<string, any> | undefined,
  property: string,
  fallback: any
) => {
  if (!changes) return fallback

  return Object.prototype.hasOwnProperty.call(changes, property)
    ? changes[property]
    : fallback
}

export const sortAndFilterRegItems = (
  regItems: Partial<RegItem>[],
  appliedSortsAndFilters: AppliedSortsAndFilters
) => {
  const { filters, sort } = appliedSortsAndFilters

  return sortRegItems(filterRegItems(regItems, filters), sort)
}

export const filterRegItems = (
  regItems: Partial<RegItem>[],
  filters: AppliedSortsAndFilters['filters']
) => {
  let filteredRegItems = [...regItems]

  if (
    filters.category !== undefined &&
    filters.category !== defaultAppliedSortsAndFilters.filters.category
  ) {
    filteredRegItems = regItems.filter((regItem) => {
      if (Number.isInteger(filters.category)) {
        return regItem.categoryId === filters.category
      }

      if (filters.category === 'Most wanted') {
        return regItem.isFavorite
      }

      // Always return false when purchased is selected. It will be populated
      // by the reservations section so we only need to hide all un-purchased reg items.
      if (filters.category === 'Purchased') {
        return false
      }

      return true
    })
  }

  if (filters.price) {
    filteredRegItems = filteredRegItems.filter((regItem) => {
      const price = getRegItemPriceForComparison(regItem)

      return (
        filters.price &&
        price >= filters.price.min &&
        price <= filters.price.max
      )
    })
  }

  if (filters.stores && filters.stores.length > 0) {
    filteredRegItems = filteredRegItems.filter((regItem) => {
      if (!regItem.offers) return false

      return regItem.offers.some(
        (offer) =>
          filters.stores && filters.stores.includes(offer.storeDisplayName)
      )
    })
  }

  return filteredRegItems
}

const sortRegItems = (
  regItems: Partial<RegItem>[],
  sort: string | undefined
) => {
  switch (sort) {
    case 'priceLowToHigh':
      return regItems.sort((a, b) => {
        const aPrice = getRegItemPriceForComparison(a)
        const bPrice = getRegItemPriceForComparison(b)

        return aPrice - bPrice
      })
    case 'priceHighToLow':
      return regItems.sort((a, b) => {
        const aPrice = getRegItemPriceForComparison(a)
        const bPrice = getRegItemPriceForComparison(b)

        return bPrice - aPrice
      })
    default:
      return regItems
  }
}

const getRegItemPriceForComparison = (regItem: Partial<RegItem>) => {
  let price: number

  if (regItem.priceDetails) {
    const { currentPrice } = getPriceTagPropsFromPriceDetails(
      regItem.priceDetails
    )

    price = Number(currentPrice || Number(regItem.price))
  } else {
    price = Number(regItem.price)
  }

  return price || 0
}

const previewFilterResults = (
  regItems: Partial<RegItem>[],
  changedSortsAndFilters: Partial<AppliedSortsAndFilters>,
  appliedSortsAndFilters: AppliedSortsAndFilters
) => {
  const mergedPreviewSortAndFilters = mergeSortsAndFilters(
    changedSortsAndFilters,
    appliedSortsAndFilters
  )

  const filteredRegItems = filterRegItems(
    regItems,
    mergedPreviewSortAndFilters.filters
  )

  return { count: filteredRegItems.length }
}

export const getPreviewFilterResultsClosure =
  (regItems: Partial<RegItem>[]) =>
  (
    changedSortsAndFilters: Partial<AppliedSortsAndFilters>,
    appliedSortsAndFilters: AppliedSortsAndFilters
  ) =>
    previewFilterResults(
      regItems,
      changedSortsAndFilters,
      appliedSortsAndFilters
    )

export const withProviders = (Component: React.FC) => () => (
  <AppliedSortsAndFiltersContextProvider>
    <Component />
  </AppliedSortsAndFiltersContextProvider>
)
